import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon, Spinner, Popover } from "@shopify/polaris";
import { Clock, Check, Eye, ExternalLink, Phone } from "lucide-react";
import { ChatIcon } from "@shopify/polaris-icons";
import { toast } from "sonner";
import { RootState, AppDispatch } from "configureStore";
import { useAppSelector } from "hooks";
import UserAvatar from "components/user-avatar/user-avatar";
import {
  setModalxComponent,
  setModalxComponentProps,
  setIsModalxOpen,
} from "components/modalx/modalx.slice";
import { formatLastSeen } from "features/presence/format-last-seen";
import { useCalls } from "features/calls/calls-provider";
import { ChatMessage, ChatReader, Conversation } from "types/Chat";
import {
  sendMessage as sendMessageApi,
  editMessage as editMessageApi,
  deleteMessage as deleteMessageApi,
  markConversationRead,
  fetchReaders,
  addReaction,
  removeReaction,
} from "services/chat.service";
import { MessageStatus } from "./message-status";
import {
  fetchMessagesThunk,
  openConversation,
  readLastConversation,
  addOptimisticMessage,
  replaceOptimisticMessage,
  removeOptimisticMessage,
  applyMessageEdited,
  applyMessageDeleted,
  applyReactionUpdate,
  clearUnread,
  openMiniChat,
} from "./chat.slice";
import ChatComposer from "./chat-composer";
import ChatMessageContent from "./chat-message-content";
import { isChatAttachmentImage } from "./chat-attachment-utils";
import { uploadChatFileMessage } from "./chat-file-upload";
import {
  ReactionPills,
  ReactionPicker,
  computeOptimisticReactions,
} from "./chat-reactions";
import { FINGER_PADDING, SHOW_ON_HOVER_OR_TOUCH } from "./chat-touch";

interface Props {
  companyId: string;
  expanded: boolean;
}

const ChatTab: React.FC<Props> = ({ companyId, expanded }) => {
  const dispatch: AppDispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "en") as "lt" | "en";
  const session = useAppSelector((state) => state.authReducer.session);
  const myUserId = session?.id;

  const { companyId: routeCompanyId } = useParams<{ companyId: string }>();
  const {
    conversations,
    conversationsLoaded,
    openConversationId,
    openConversationCompanyId,
    activeMiniChats,
    threads,
  } = useSelector((state: RootState) => state.chatReducer);
  const scopedOpenConversationId =
    openConversationCompanyId === companyId ? openConversationId : null;

  // Note: socket listeners (useChatSocket) and the initial conversations fetch
  // are mounted at the panel level (OnlineUsersPanel) so they keep working
  // when the user is on the Online tab. ChatTab only handles open-thread state.

  // Restore last open conversation from localStorage
  useEffect(() => {
    if (!companyId || !conversationsLoaded || scopedOpenConversationId) return;
    const last = readLastConversation(companyId);
    if (last && conversations.find((c) => c.id === last)) {
      dispatch(openConversation({ companyId, conversationId: last }));
    }
  }, [companyId, conversationsLoaded, scopedOpenConversationId, conversations, dispatch]);

  // Auto-load messages when a conversation is opened
  useEffect(() => {
    if (!companyId || !scopedOpenConversationId) return;
    if (threads[scopedOpenConversationId]?.historyLoaded) return; // already loaded
    dispatch(
      fetchMessagesThunk({ companyId, conversationId: scopedOpenConversationId })
    );
  }, [companyId, scopedOpenConversationId, threads, dispatch]);

  const openConvo = useMemo(
    () => conversations.find((c) => c.id === scopedOpenConversationId) || null,
    [conversations, scopedOpenConversationId]
  );

  const openChatThread = (c: Conversation) => {
    const isCompany = c.type === "company";
    const title = isCompany ? t("company_chat") : c.otherParticipant?.name || "";
    const crossCompanyMini = activeMiniChats.find(
      (m) =>
        String(m.conversationId) === String(c.id) &&
        String(m.companyId) !== String(routeCompanyId || companyId)
    );
    if (crossCompanyMini) {
      dispatch(
        openMiniChat({
          conversationId: c.id,
          companyId: crossCompanyMini.companyId,
          title,
          avatarFileId: c.otherParticipant?.avatarFileId,
          conversationType: c.type,
          forceExpand: true,
        })
      );
      return;
    }
    if (String(companyId) !== String(routeCompanyId)) {
      dispatch(
        openMiniChat({
          conversationId: c.id,
          companyId,
          title,
          avatarFileId: c.otherParticipant?.avatarFileId,
          conversationType: c.type,
          forceExpand: true,
        })
      );
      return;
    }
    dispatch(openConversation({ companyId, conversationId: c.id }));
  };

  if (!expanded) {
    // Collapsed: a single hint icon — keeps the panel narrow but indicates the tab content
    return (
      <div className="flex flex-col items-center pt-4 text-gray-500 gap-2">
        <span className="block w-5 h-5 opacity-60">
          <Icon source={ChatIcon as any} tone="subdued" />
        </span>
        <div className="text-[10px] uppercase tracking-wide">{t("chats")}</div>
      </div>
    );
  }

  if (openConvo) {
    return (
      <ChatThreadView
        companyId={companyId}
        conversationId={openConvo.id}
        title={
          openConvo.type === "company"
            ? t("company_chat")
            : openConvo.otherParticipant?.name || ""
        }
        avatar={
          openConvo.type === "dm" && openConvo.otherParticipant
            ? {
                id: openConvo.otherParticipant.id,
                name: openConvo.otherParticipant.name,
                avatarFileId: openConvo.otherParticipant.avatarFileId,
              }
            : null
        }
        myUserId={myUserId}
        onBack={() =>
          dispatch(openConversation({ companyId, conversationId: null }))
        }
      />
    );
  }

  // Conversations list view
  if (!conversationsLoaded) {
    return (
      <div className="flex justify-center py-6">
        <Spinner size="small" />
      </div>
    );
  }

  if (!conversations.length) {
    return <div className="px-3 py-4 text-sm text-gray-500">{t("no_conversations")}</div>;
  }

  return (
    <div>
      {conversations.map((c) => {
        const preview = c.lastMessage?.preview || "";
        const time = c.lastMessage?.createdAt
          ? formatLastSeen(c.lastMessage.createdAt, t, lang)
          : "";
        const isCompany = c.type === "company";
        return (
          <div
            key={c.id}
            onClick={() => openChatThread(c)}
            className="px-3 py-2 flex items-center gap-3 hover:bg-gray-800 cursor-pointer border-b border-gray-800 group"
          >
            {isCompany ? (
              <div
                className="w-11 h-11 rounded-lg flex items-center justify-center bg-emerald-700 text-white font-bold text-lg shrink-0"
                title={t("company_chat")}
              >
                #
              </div>
            ) : c.otherParticipant ? (
              <UserAvatar
                companyId={companyId}
                user={{
                  id: c.otherParticipant.id,
                  name: c.otherParticipant.name,
                  avatarFileId: c.otherParticipant.avatarFileId,
                }}
                size={44}
              />
            ) : (
              <div className="w-11 h-11 rounded-lg bg-gray-700 shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">
                {isCompany
                  ? t("company_chat")
                  : c.otherParticipant?.name || ""}
              </div>
              <div className="text-xs text-gray-400 truncate">
                {preview || t("no_messages_yet")}
              </div>
              <div className="text-[10px] max-md:text-xs text-gray-500">{time}</div>
            </div>
            {c.unreadCount > 0 && (
              <span className="bg-red-500 text-white text-[10px] max-md:text-xs px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                {c.unreadCount > 99 ? "99+" : c.unreadCount}
              </span>
            )}
            {/* a floating window is no use on a phone, where the chat already fills the screen */}
            <button
              type="button"
              className="p-2 hover:bg-gray-700 rounded max-md:hidden"
              onClick={(e) => {
                e.stopPropagation();
                dispatch(openMiniChat({ 
                  conversationId: c.id, 
                  companyId, 
                  title: isCompany ? t("company_chat") : c.otherParticipant?.name || "", 
                  avatarFileId: c.otherParticipant?.avatarFileId,
                  conversationType: c.type,
                  forceExpand: true,
                }));
              }}
              title={t("pop_out_chat", { defaultValue: "Pop out chat" })}
            >
              <ExternalLink size={14} className="text-gray-400" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

interface ThreadViewProps {
  companyId: string;
  conversationId: string;
  title: string;
  avatar: { id: string; name: string; avatarFileId: string | null } | null;
  myUserId: string | undefined;
  onBack: () => void;
}

// Keep in sync with `Messages.EDIT_WINDOW_MS` in erp-backend/api/models/Messages.js.
// The server is the source of truth (rejects late edits with 403); this constant
// only controls whether the UI shows the "Edit" affordance.
const EDIT_WINDOW_MS = 5 * 60 * 1000;

const ChatThreadView: React.FC<ThreadViewProps> = ({
  companyId,
  conversationId,
  title,
  avatar,
  myUserId,
  onBack,
}) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { enabled: callsEnabled, busyConversationId, startCall } = useCalls();
  const thread = useSelector(
    (state: RootState) => state.chatReducer.threads[conversationId]
  );
  const convo = useSelector((state: RootState) =>
    state.chatReducer.conversations.find((c) => c.id === conversationId)
  );

  const openProfile = (userId: string) => {
    dispatch(setModalxComponent("UserPublicProfileModal"));
    dispatch(setModalxComponentProps({ companyId, userId }));
    dispatch(setIsModalxOpen(true));
  };

  // `avatar` is only set for a DM, so it doubles as "there is someone to call"
  const peer = useMemo(
    () => (avatar?.id ? { userId: String(avatar.id), name: avatar.name || title } : null),
    [avatar?.id, avatar?.name, title]
  );

  // Build a lookup from userId → { name, avatarFileId } for rendering message
  // headers (Slack-style "avatar + name" above the first bubble in a run).
  // Sources: presence (self + online + offline) and the DM's other participant.
  const presenceSelf = useSelector((state: RootState) => state.presenceReducer.self);
  const presenceOnline = useSelector((state: RootState) => state.presenceReducer.online);
  const presenceOffline = useSelector((state: RootState) => state.presenceReducer.offline);
  const userById = useMemo(() => {
    const m = new Map<string, { id: string; name: string; avatarFileId: string | null }>();
    if (presenceSelf) m.set(presenceSelf.id, {
      id: presenceSelf.id, name: presenceSelf.name, avatarFileId: presenceSelf.avatarFileId,
    });
    for (const u of presenceOnline) m.set(u.id, { id: u.id, name: u.name, avatarFileId: u.avatarFileId });
    for (const u of presenceOffline) m.set(u.id, { id: u.id, name: u.name, avatarFileId: u.avatarFileId });
    if (avatar) m.set(avatar.id, avatar);
    return m;
  }, [presenceSelf, presenceOnline, presenceOffline, avatar]);
  const isLoading = !thread;
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const firstUnreadRef = useRef<HTMLDivElement | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  // Only one reaction picker can be open at a time across the whole thread.
  // Tracking the message id (rather than per-component state) closes any
  // already-open picker as soon as the user opens another one.
  const [openPickerId, setOpenPickerId] = useState<string | null>(null);

  // Snapshot of `lastReadAt` taken when this conversation first opens. Stays
  // stable across the session so the "new messages" divider doesn't disappear
  // as soon as auto-mark-read fires. `undefined` = not captured yet, `null` =
  // captured (no prior reads), `number` = captured timestamp.
  const [initialLastReadAt, setInitialLastReadAt] = useState<number | null | undefined>(
    undefined
  );
  // Whether we've performed the initial scroll (to first-unread or to bottom).
  const initialScrollDoneRef = useRef(false);

  // Reset capture state when switching conversations
  useEffect(() => {
    setInitialLastReadAt(undefined);
    initialScrollDoneRef.current = false;
    setOpenPickerId(null);
  }, [conversationId]);

  // Capture `lastReadAt` once the conversation row is in state
  useEffect(() => {
    if (initialLastReadAt !== undefined) return;
    if (!convo) return;
    setInitialLastReadAt(convo.lastReadAt ?? null);
  }, [convo, initialLastReadAt]);

  // Index of the first unread message from someone else (relative to the snapshot)
  const firstUnreadIndex = useMemo(() => {
    if (!thread || initialLastReadAt === undefined) return -1;
    const cutoff = initialLastReadAt || 0;
    return thread.messages.findIndex(
      (m) =>
        String(m.sender) !== String(myUserId) &&
        !m.deletedAt &&
        m.createdAt > cutoff
    );
  }, [thread, initialLastReadAt, myUserId]);

  const unreadInThread = useMemo(() => {
    if (firstUnreadIndex < 0 || !thread) return 0;
    let n = 0;
    for (let i = firstUnreadIndex; i < thread.messages.length; i++) {
      const m = thread.messages[i];
      if (String(m.sender) !== String(myUserId) && !m.deletedAt) n++;
    }
    return n;
  }, [firstUnreadIndex, thread, myUserId]);

  // Initial scroll: to first-unread divider, else to bottom. Runs once per
  // conversation open (re-armed by the conversationId-change effect above).
  useEffect(() => {
    if (initialScrollDoneRef.current) return;
    if (!thread || thread.messages.length === 0) return;
    if (initialLastReadAt === undefined) return;
    requestAnimationFrame(() => {
      if (firstUnreadIndex >= 0 && firstUnreadRef.current) {
        firstUnreadRef.current.scrollIntoView({ block: "center" });
      } else if (scrollerRef.current) {
        scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
      }
      initialScrollDoneRef.current = true;
    });
  }, [thread, initialLastReadAt, firstUnreadIndex]);

  // Auto-scroll to bottom on incoming messages, but only if user is already
  // near the bottom (don't yank them away from older content they're reading).
  useEffect(() => {
    if (!initialScrollDoneRef.current) return;
    const el = scrollerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (distanceFromBottom < 200) {
      el.scrollTop = el.scrollHeight;
    }
  }, [thread?.messages.length]);

  // Mark as read on open + when new incoming messages arrive — debounced so
  // that a chatty stream of messages doesn't bombard the server with reads.
  const markReadTimeoutRef = useRef<number | null>(null);
  useEffect(() => {
    if (!companyId || !conversationId) return;
    if (markReadTimeoutRef.current) {
      window.clearTimeout(markReadTimeoutRef.current);
    }
    markReadTimeoutRef.current = window.setTimeout(async () => {
      try {
        const res = await markConversationRead(companyId, conversationId);
        dispatch(clearUnread({ conversationId, lastReadAt: res.lastReadAt }));
      } catch {
        /* ignore */
      }
    }, 800);
    return () => {
      if (markReadTimeoutRef.current) {
        window.clearTimeout(markReadTimeoutRef.current);
        markReadTimeoutRef.current = null;
      }
    };
  }, [companyId, conversationId, dispatch, thread?.messages.length]);

  const handleSend = async (content: string) => {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const optimistic: ChatMessage = {
      id: tempId,
      conversationId,
      sender: myUserId || "",
      content,
      attachment: null,
      createdAt: Date.now(),
      editedAt: null,
      deletedAt: null,
      reactions: [],
      uploadStatus: "pending",
    };
    dispatch(addOptimisticMessage(optimistic));
    try {
      const real = await sendMessageApi(companyId, conversationId, { content });
      dispatch(replaceOptimisticMessage({ tempId, message: real }));
    } catch (err: any) {
      dispatch(removeOptimisticMessage({ conversationId, tempId }));
      toast.error(err?.response?.data?.error || t("error"));
      throw err;
    }
  };

  const handleFileSelected = async (file: File) => {
    await uploadChatFileMessage({
      dispatch,
      companyId,
      conversationId,
      myUserId,
      file,
      fileUploadFailedMessage: t("file_upload_failed"),
      onError: toast.error,
    });
  };

  const startEdit = (msg: { id: string; content: string }) => {
    setEditingId(msg.id);
    setEditingValue(msg.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const next = editingValue.trim();
    if (!next) return;
    try {
      const updated = await editMessageApi(companyId, editingId, next);
      dispatch(applyMessageEdited({
        conversationId,
        messageId: updated.id,
        content: updated.content,
        editedAt: updated.editedAt || Date.now(),
      }));
      cancelEdit();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t("error"));
    }
  };

  const deleteOwn = async (messageId: string) => {
    try {
      await deleteMessageApi(companyId, messageId);
      dispatch(applyMessageDeleted({ conversationId, messageId }));
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t("error"));
    }
  };

  /**
   * Toggle a reaction on a message. Optimistic: dispatch the new array
   * immediately, then call the server. The socket echo (or the response
   * itself) replaces our local guess with the authoritative summary.
   *
   * Server is the toggle source of truth — we always POST. If the user
   * already has the same emoji, the controller treats it as DELETE.
   */
  const handleToggle = async (messageId: string, emoji: string) => {
    const msg = thread?.messages.find((m) => m.id === messageId);
    if (!msg) return;
    if (messageId.startsWith("temp-")) return; // server doesn't know about it yet
    if (msg.deletedAt) return;

    const next = computeOptimisticReactions(msg.reactions || [], emoji);
    dispatch(applyReactionUpdate({ conversationId, messageId, reactions: next }));

    const myExisting = (msg.reactions || []).find((r) => r.me);
    const isToggleOff = !!myExisting && myExisting.emoji === emoji;

    try {
      const reactions = isToggleOff
        ? await removeReaction(companyId, messageId)
        : await addReaction(companyId, messageId, emoji);
      // Reconcile in case our optimistic guess differed from server truth.
      dispatch(applyReactionUpdate({ conversationId, messageId, reactions }));
    } catch (err: any) {
      // Roll back to the previous server state. Easiest path: replay the
      // pre-toggle array we still have a reference to.
      dispatch(applyReactionUpdate({
        conversationId,
        messageId,
        reactions: msg.reactions || [],
      }));
      toast.error(err?.response?.data?.error || t("error"));
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header with back button */}
      <div className="border-b border-gray-700 flex items-center gap-2 px-2 py-2">
        <button
          type="button"
          onClick={onBack}
          className="p-1.5 max-lg:p-3 max-lg:min-w-11 rounded hover:bg-gray-800 text-gray-300 text-sm"
          aria-label={t("back")}
        >
          ←
        </button>
        {avatar ? (
          // The person's card lives here now: the list opens the chat, and who
          // they are is one click further in, on their name.
          <button
            type="button"
            onClick={() => openProfile(avatar.id)}
            className="flex items-center gap-2 min-w-0 flex-1 text-left rounded px-1 -mx-1 max-lg:min-h-11 hover:bg-gray-800"
            title={t("user_profile")}
          >
            <UserAvatar companyId={companyId} user={avatar} size={32} />
            <span className="text-sm font-medium truncate">{title}</span>
          </button>
        ) : (
          <span className="text-sm font-medium truncate flex-1">{title}</span>
        )}
        {/* calls are 1:1 — the company chat has no one to ring */}
        {callsEnabled && peer && (
          <button
            type="button"
            onClick={() => startCall(companyId, conversationId, peer)}
            disabled={!!busyConversationId}
            className="p-1.5 max-lg:p-3 rounded hover:bg-gray-800 text-gray-300 disabled:opacity-40"
            title={t("call_start")}
            aria-label={t("call_start")}
          >
            <Phone size={16} className="max-lg:h-5 max-lg:w-5" />
          </button>
        )}
      </div>

      {/* Messages area */}
      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-3 flex flex-col gap-2 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-gray-700"
      >
        {isLoading ? (
          <div className="flex justify-center py-6"><Spinner size="small" /></div>
        ) : thread.messages.length === 0 ? (
          <div className="text-xs text-gray-500 text-center py-6">{t("no_messages_yet")}</div>
        ) : (
          thread.messages.map((m, idx) => {
            const mine = String(m.sender) === String(myUserId);
            const isOptimistic = m.id.startsWith("temp-");
            const isEditing = editingId === m.id;
            const canEdit = mine && !m.deletedAt && !isOptimistic
              && (Date.now() - m.createdAt < EDIT_WINDOW_MS);
            const canDelete = mine && !m.deletedAt && !isOptimistic;
            const showUnreadDivider = idx === firstUnreadIndex && unreadInThread > 0;
            const prev = idx > 0 ? thread.messages[idx - 1] : null;
            // Slack-style grouping: show avatar + name on the FIRST message of
            // a run from the same author. Own messages never get a header.
            const showAuthorHeader =
              !mine &&
              (idx === 0 ||
                showUnreadDivider ||
                !prev ||
                String(prev.sender) !== String(m.sender) ||
                m.createdAt - prev.createdAt > 5 * 60 * 1000);
            const author = userById.get(m.sender) || { id: m.sender, name: "", avatarFileId: null };
            return (
              <React.Fragment key={m.id}>
                {showUnreadDivider && (
                  <div
                    ref={firstUnreadRef}
                    className="my-2 flex items-center gap-2 text-[10px] uppercase tracking-wide text-red-400"
                  >
                    <div className="flex-1 h-px bg-red-500/40" />
                    <span>
                      {t("new_messages_count", {
                        count: unreadInThread,
                        defaultValue: `${unreadInThread} new`,
                      })}
                    </span>
                    <div className="flex-1 h-px bg-red-500/40" />
                  </div>
                )}
                {showAuthorHeader && (
                  <div className="flex items-center gap-2 mt-1 mb-0.5 self-start">
                    <UserAvatar
                      companyId={companyId}
                      user={author}
                      size={20}
                    />
                    <span className="text-xs text-gray-300 font-medium truncate">
                      {author.name || m.sender}
                    </span>
                  </div>
                )}
                <div
                  className={`flex flex-col max-w-[85%] group ${mine ? "self-end items-end" : "self-start items-start"}`}
                >
                {isEditing ? (
                  <div className="flex flex-col w-full gap-1">
                    <textarea
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      rows={2}
                      className="w-full bg-gray-800 text-gray-100 text-base lg:text-sm px-2 py-1.5 rounded border border-blue-500 focus:outline-none"
                      autoFocus
                    />
                    <div className="flex gap-1 justify-end">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="text-[10px] max-md:text-xs max-lg:min-h-11 max-lg:px-3 px-2 py-0.5 rounded text-gray-400 hover:bg-gray-800"
                      >
                        {t("cancel")}
                      </button>
                      <button
                        type="button"
                        onClick={saveEdit}
                        className="text-[10px] max-md:text-xs max-lg:min-h-11 max-lg:px-3 px-2 py-0.5 rounded bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {t("save")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className={
                      "rounded-lg text-sm whitespace-pre-wrap break-words " +
                      (() => {
                        const displayName = m.attachment?.title || m.attachment?.name || "";
                        const isImage = isChatAttachmentImage(displayName, m.localPreviewUrl);
                        if (!m.deletedAt && !m.content && isImage) return "p-1 ";
                        return "px-3 py-1.5 ";
                      })() +
                      (mine
                        ? `bg-blue-600 text-white ${isOptimistic ? "opacity-60" : ""}`
                        : "bg-gray-800 text-gray-100")
                    }
                  >
                    {m.deletedAt ? (
                      <span className="italic text-gray-400">{t("message_deleted")}</span>
                    ) : (
                      <ChatMessageContent message={m} mine={mine} companyId={companyId} />
                    )}
                  </div>
                )}
                {!m.deletedAt && (
                  <ReactionPills
                    reactions={m.reactions || []}
                    onToggle={(e) => handleToggle(m.id, e)}
                  />
                )}
                <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-2">
                  <span>
                    {new Date(m.createdAt).toLocaleTimeString(undefined, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    {m.editedAt && !m.deletedAt && (
                      <span className="italic"> ({t("edited")})</span>
                    )}
                  </span>
                  {!isEditing && !m.deletedAt && !isOptimistic && (
                    <ReactionPicker
                      open={openPickerId === m.id}
                      onOpenChange={(o) => setOpenPickerId(o ? m.id : null)}
                      onPick={(e) => handleToggle(m.id, e)}
                    />
                  )}
                  {!isEditing && (canEdit || canDelete) && (
                    <span className={`${SHOW_ON_HOVER_OR_TOUCH} flex gap-1`}>
                      {canEdit && (
                        <button
                          type="button"
                          className={`${FINGER_PADDING} hover:text-gray-300`}
                          onClick={() => startEdit({ id: m.id, content: m.content })}
                        >
                          {t("edit")}
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className={`${FINGER_PADDING} hover:text-red-400`}
                          onClick={() => deleteOwn(m.id)}
                        >
                          {t("delete")}
                        </button>
                      )}
                    </span>
                  )}
                  {mine && !m.deletedAt && convo && (
                    <MessageStatus
                      message={m}
                      conversation={convo}
                      companyId={companyId}
                    />
                  )}
                </div>
                </div>
              </React.Fragment>
            );
          })
        )}
      </div>

      <ChatComposer onSend={handleSend} onFileSelected={handleFileSelected} />
    </div>
  );
};

export default ChatTab;
